import { z } from 'zod';
import type { DayOfWeek, OpeningHoursSpecification } from '../content/studio';
import { DayOfWeekSchema } from '../content/studio';

/** Formulare → Reservation request → Rules (Level 1). */
export const ReservationBookingPolicySchema = z.object({
  /** Flag when (confirmedSeats + party) > capacityPercent/100 * totalSeats. */
  capacityPercent: z.number().int().min(1).max(100).default(70),
  /** ± minutes around requested time for seat aggregation. */
  timeWindowMinutes: z.number().int().min(15).max(24 * 60).default(90),
  /** Preset notice strings offered on Confirm with notice. */
  noticePresets: z.array(z.string().min(1).max(200)).max(12).default([]),
  cancelPolicy: z.enum(['anytime']).default('anytime'),
});

export type ReservationBookingPolicy = z.infer<typeof ReservationBookingPolicySchema>;

export const DEFAULT_RESERVATION_BOOKING_POLICY: ReservationBookingPolicy = {
  capacityPercent: 70,
  timeWindowMinutes: 90,
  noticePresets: [
    'Booking held for 30 minutes',
    'Tisch ist 30 Minuten für Sie reserviert',
  ],
  cancelPolicy: 'anytime',
};

export function resolveReservationBookingPolicy(
  partial: Partial<ReservationBookingPolicy> | null | undefined,
): ReservationBookingPolicy {
  const parsed = ReservationBookingPolicySchema.safeParse({
    ...DEFAULT_RESERVATION_BOOKING_POLICY,
    ...partial,
    noticePresets:
      partial?.noticePresets && partial.noticePresets.length > 0
        ? partial.noticePresets
        : DEFAULT_RESERVATION_BOOKING_POLICY.noticePresets,
  });
  return parsed.success ? parsed.data : DEFAULT_RESERVATION_BOOKING_POLICY;
}

export const LeadHoursStatusSchema = z.enum(['ok', 'outside_hours', 'unknown']);
export const LeadSeatsStatusSchema = z.enum(['ok', 'warning', 'unknown']);

export const LeadInquiryAssessmentSchema = z.object({
  hours: LeadHoursStatusSchema,
  seats: LeadSeatsStatusSchema,
  partySize: z.number().int().nonnegative().optional(),
  confirmedSeatsInWindow: z.number().int().nonnegative().optional(),
  totalSeats: z.number().int().nonnegative().optional(),
  capacityPercent: z.number().int().optional(),
  timeWindowMinutes: z.number().int().optional(),
  reservedDate: z.string().optional(),
  reservedTime: z.string().optional(),
});

export type LeadInquiryAssessment = z.infer<typeof LeadInquiryAssessmentSchema>;

export type ReservationService = 'lunch' | 'dinner';

const JS_DAY_TO_SCHEMA: DayOfWeek[] = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export function timeToMinutes(time: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time.trim());
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

export function weekdayFromIsoDate(isoDate: string): DayOfWeek | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const date = new Date(Date.UTC(y, m - 1, d));
  if (Number.isNaN(date.getTime())) return null;
  return JS_DAY_TO_SCHEMA[date.getUTCDay()] ?? null;
}

function dayKeysFromSpec(row: OpeningHoursSpecification): DayOfWeek[] {
  return Array.isArray(row.dayOfWeek) ? row.dayOfWeek : [row.dayOfWeek];
}

/**
 * True when `time` falls in an open interval for that calendar date.
 * Overnight shifts (closes < opens) supported.
 */
export function isOpenAtDateTime(
  openingHours: OpeningHoursSpecification[] | null | undefined,
  isoDate: string,
  timeHhMm: string,
): boolean | null {
  if (!openingHours || openingHours.length === 0) return null;
  const day = weekdayFromIsoDate(isoDate);
  const minutes = timeToMinutes(timeHhMm);
  if (!day || minutes == null) return null;

  const shifts = openingHours.filter((row) => dayKeysFromSpec(row).includes(day));
  if (shifts.length === 0) return false;

  for (const shift of shifts) {
    const opens = timeToMinutes(shift.opens);
    const closes = timeToMinutes(shift.closes);
    if (opens == null || closes == null) continue;
    if (closes > opens) {
      if (minutes >= opens && minutes < closes) return true;
    } else if (closes < opens) {
      // overnight e.g. 18:00–02:00
      if (minutes >= opens || minutes < closes) return true;
    }
  }
  return false;
}

export function parsePartySize(payload: Record<string, unknown>): number {
  const raw = payload.guests ?? payload.partySize ?? payload.persons;
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.max(0, Math.floor(raw));
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number.parseInt(raw.trim(), 10);
    return Number.isFinite(n) ? Math.max(0, n) : 0;
  }
  return 0;
}

export function parseReservedDateTime(payload: Record<string, unknown>): {
  date: string | null;
  time: string | null;
} {
  const date = typeof payload.date === 'string' ? payload.date.trim() : null;
  const time = typeof payload.time === 'string' ? payload.time.trim() : null;
  return {
    date: date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null,
    time: time && timeToMinutes(time) != null ? time : null,
  };
}

/** Minutes from an arbitrary epoch for window comparison (date+time local-as-UTC). */
export function reservedInstantMinutes(isoDate: string, timeHhMm: string): number | null {
  const day = weekdayFromIsoDate(isoDate);
  const mins = timeToMinutes(timeHhMm);
  if (!day || mins == null) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  return Date.UTC(y, m - 1, d) / 60000 + mins;
}

export function assessReservationInquiry(input: {
  payload: Record<string, unknown>;
  openingHours: OpeningHoursSpecification[] | null | undefined;
  totalSeats: number;
  confirmedSeatsInWindow: number;
  policy: ReservationBookingPolicy;
}): LeadInquiryAssessment {
  const { date, time } = parseReservedDateTime(input.payload);
  const partySize = parsePartySize(input.payload);

  let hours: LeadInquiryAssessment['hours'] = 'unknown';
  if (date && time) {
    const open = isOpenAtDateTime(input.openingHours, date, time);
    hours = open == null ? 'unknown' : open ? 'ok' : 'outside_hours';
  }

  let seats: LeadInquiryAssessment['seats'] = 'unknown';
  if (input.totalSeats > 0 && partySize > 0) {
    const limit = (input.policy.capacityPercent / 100) * input.totalSeats;
    seats =
      input.confirmedSeatsInWindow + partySize > limit ? 'warning' : 'ok';
  } else if (input.totalSeats <= 0) {
    seats = 'unknown';
  } else {
    seats = 'ok';
  }

  return {
    hours,
    seats,
    partySize,
    confirmedSeatsInWindow: input.confirmedSeatsInWindow,
    totalSeats: input.totalSeats,
    capacityPercent: input.policy.capacityPercent,
    timeWindowMinutes: input.policy.timeWindowMinutes,
    reservedDate: date ?? undefined,
    reservedTime: time ?? undefined,
  };
}

export function summarizeLeadAssessment(
  assessment: LeadInquiryAssessment | null | undefined,
  locale = 'en',
): string {
  if (!assessment) {
    return locale.startsWith('de') ? 'Keine Prüfung' : 'No assessment';
  }
  const parts: string[] = [];
  if (locale.startsWith('de')) {
    if (assessment.hours === 'outside_hours') parts.push('Außerhalb der Öffnungszeiten');
    else if (assessment.hours === 'ok') parts.push('Öffnungszeiten OK');
    if (assessment.seats === 'warning') {
      parts.push(
        `Sitzplätze: Warnung (${assessment.confirmedSeatsInWindow ?? 0}+${assessment.partySize ?? 0} > ${assessment.capacityPercent ?? 70}% von ${assessment.totalSeats ?? 0})`,
      );
    } else if (assessment.seats === 'ok') parts.push('Sitzplätze OK');
  } else {
    if (assessment.hours === 'outside_hours') parts.push('Outside opening hours');
    else if (assessment.hours === 'ok') parts.push('Hours OK');
    if (assessment.seats === 'warning') {
      parts.push(
        `Seats warning (${assessment.confirmedSeatsInWindow ?? 0}+${assessment.partySize ?? 0} > ${assessment.capacityPercent ?? 70}% of ${assessment.totalSeats ?? 0})`,
      );
    } else if (assessment.seats === 'ok') parts.push('Seats OK');
  }
  return parts.length > 0 ? parts.join(' · ') : locale.startsWith('de') ? 'Keine Flags' : 'Clear';
}

/** Re-export for callers that already import DayOfWeekSchema from this module path. */
export { DayOfWeekSchema };
